import React, { useContext } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { storeRequest } from "../../api/requests";
import { attachRecaptcha } from "../../api/accounts";
import allowedCities from "../static/allowedCities";
import AuthContext from "../../context/AuthContext";
import {
  incrementActiveRequestCount,
  incrementTotalRequestCount,
} from "../../api/stats";
import { updateUserData } from "../../api/users";
import Swal from "sweetalert2";
import hospital from "../assets/hospital.webp";
import hospitalIos from "../assets/hospital.png";
import otpHelper from "../users/otpHelper";

export default function RequestSubmissionForm(props: any) {
  const { user }: any = useContext(AuthContext);
  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      confirmButton: "btn btn-primary mr-2",
      cancelButton: "btn btn-danger ml-2",
    },
    buttonsStyling: false,
  });

  const onSuccessUnregistered = (
    values: any,
    contact: string,
    resetForm: any,
    setSubmitting: Function
  ) => {
    values.contact = contact;
    const requester = user ? user.uid : false; // passing the uid if request made by logged user
    storeRequest(values, requester).then((id) => {
      incrementActiveRequestCount();
      incrementTotalRequestCount();
      // if (user) {
      //   user.details.stats.requestsMade++;
      //   updateUserData(user.details, user.uid, user.userType);
      // }
      swalWithBootstrapButtons.fire({
        icon: "success",
        title: "Request Submitted",
        text: `Request ID: ${id}`,
        footer:
          "<strong>Keep this ID safe. You will need it to check request status.</strong>",
      });
    });
    resetForm({});
    setSubmitting(false);
    setTimeout(() => {}, 2000);
  };

  const onSuccessOrganization = (
    values: any,
    contact: string,
    resetForm: any,
    setSubmitting: Function
  ) => {
    values.contact = contact;
    const requester = user ? user.uid : false; // passing the uid if request made by logged user
    storeRequest(values, requester).then((id) => {
      incrementActiveRequestCount();
      incrementTotalRequestCount();
      // user.details.stats.requestsMade++;
      // updateUserData(user.details, user.uid, user.userType);
      swalWithBootstrapButtons.fire({
        icon: "success",
        title: "Request Submitted",
        text: `Request ID: ${id}`,
        footer:
          "<strong>Keep this ID safe. You will need it to check request status.</strong>",
      });
    });
    resetForm({});
    setSubmitting(false);
    setTimeout(() => {}, 2000);
  };

  React.useEffect(() => {
    attachRecaptcha("recaptcha-container");
  }, []);

  return (
    <div id={props.id} className={props.class}>
      <Formik
        initialValues={{
          name: "",
          city: "",
          email: "",
          bloodGroup: "",
          hospital: "",
          urgency: "",
          contact: "",
          transportation: "",
          type: "",
          isActive: true,
        }}
        validationSchema={Yup.object({
          name: Yup.string()
            .max(20, "Must be 20 characters or less")
            .required("Required"),
          city: Yup.string()
            .oneOf(allowedCities, "Invalid city")
            .required("Select one of the options"),
          email: Yup.string()
            .email("Invalid email address")
            .required("Required"),
          bloodGroup: Yup.string()
            .oneOf(
              ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"],
              "Invalid Blood Group"
            )
            .required("Select one of the options"),
          hospital: Yup.string()
            .max(30, "Must be 30 characters or less")
            .required("Enter hospital name"),
          urgency: Yup.string()
            .oneOf(["Yes", "No"], "Invalid Option")
            .required("Select one of the options"),
          transportation: Yup.string()
            .oneOf(["Yes", "No"], "Invalid Option")
            .required("Select one of the options"),
          contact: Yup.string()
            .matches(
              /(03)\d{9}/,
              "Invalid phone number. Please enter number only in format 03xxxxxxxxx"
            )
            .min(11)
            .max(11)
            .required("Phone number is required"),
          type: Yup.string()
            .oneOf(["Plasma", "Blood"], "Invalid Option")
            .required("Select one of the options"),
        })}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          const contact = `92${values.contact.substr(1)}`;
          if (user && user.userType === "requester") {
            if (user.details.requestsCounter === 5) {
              swalWithBootstrapButtons.fire({
                icon: "warning",
                title: "Limit Reached",
                text: "You can only make 5 requests every 24 hours. Wait for the limit to reset and try again.",
                footer:
                  "<strong>The limit is reset at 12 am, daily.</strong>",
              });
            } else {
                otpHelper(contact, values, setSubmitting, true, true, false, () =>
                onSuccessOrganization(values, contact, resetForm, setSubmitting)
              );
            }
          } else {
            otpHelper(contact, values, setSubmitting, true, false, false, () =>
              onSuccessUnregistered(values, contact, resetForm, setSubmitting)
            );
          }
          setSubmitting(false);
        }}
      >
        {({ touched, errors, isSubmitting, values }) => (
          <Form>
            <h2 className="request-submission-form-heading">
              <picture>
                <source
                  className="home-buttons-icons"
                  srcSet={hospital}
                  type="image/webp"
                />
                <source
                  className="home-buttons-icons"
                  srcSet={hospitalIos}
                  type="image/png"
                />
                <img className="home-buttons-icons" src={hospital} alt="icon" />
              </picture>
              Submit a Blood Request
            </h2>
            <p className="form-text text-muted">Enter Patient Details</p>
            <div className="form-group">
              <Field
                className={`form-control ${
                  touched.name && errors.name ? "is-invalid" : ""
                } ${errors.name || values.name === "" ? "" : "is-valid"}
              `}
                name="name"
                type="text"
                placeholder="Full Name"
              />
              <small className="form-text text-muted">
                Full Name e.g: Aslam Mushtaq
              </small>
              <ErrorMessage
                name="name"
                component="p"
                className="invalid-feedback"
              />
            </div>
            <div className="form-group">
              <Field
                className={`form-control ${
                  touched.contact && errors.contact ? "is-invalid" : ""
                } ${errors.contact || values.contact === "" ? "" : "is-valid"}`}
                name="contact"
                type="text"
                placeholder="Mobile Number"
              />
              <small className="form-text text-muted">
                Mobile Number e.g: 03xxxxxxxxx
              </small>
              <ErrorMessage
                name="contact"
                component="p"
                className="invalid-feedback"
              />
            </div>
            <div className="form-group">
              <Field
                className={`form-control ${
                  touched.city && errors.city ? "is-invalid" : ""
                } ${errors.city || values.city === "" ? "" : "is-valid"}`}
                name="city"
                as="select"
              >
                <option value="">Select your city</option>
                {allowedCities.map((city, index) => {
                  return (
                    <option key={index} value={city}>
                      {city}
                    </option>
                  );
                })}
              </Field>
              <ErrorMessage
                component="div"
                name="city"
                className="invalid-feedback"
              />
            </div>
            <div className="form-group">
              <Field
                className={`form-control ${
                  touched.email && errors.email ? "is-invalid" : ""
                } ${errors.email || values.email === "" ? "" : "is-valid"}`}
                name="email"
                type="email"
                placeholder="Email"
              />
              <small className="form-text text-muted">
                Valid Email e.g: xyz@xyz.com
              </small>
              <ErrorMessage
                name="email"
                component="p"
                className="invalid-feedback"
              />
            </div>
            <div className="form-group">
              <Field
                className={`form-control ${
                  touched.bloodGroup && errors.bloodGroup ? "is-invalid" : ""
                } ${
                  errors.bloodGroup || values.bloodGroup === ""
                    ? ""
                    : "is-valid"
                }`}
                name="bloodGroup"
                as="select"
              >
                <option value="">Select your blood group</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
              </Field>
              <ErrorMessage
                name="bloodGroup"
                component="p"
                className="invalid-feedback"
              />
            </div>
            <div className="form-group">
              <Field
                className={`form-control ${
                  touched.urgency && errors.urgency ? "is-invalid" : ""
                } ${errors.urgency || values.urgency === "" ? "" : "is-valid"}`}
                name="urgency"
                as="select"
              >
                <option value="">Is it Urgent?</option>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </Field>

              <ErrorMessage
                name="urgency"
                component="p"
                className="invalid-feedback"
              />
            </div>
            <div className="form-group">
              <Field
                className={`form-control ${
                  touched.transportation && errors.transportation
                    ? "is-invalid"
                    : ""
                } ${
                  errors.transportation || values.transportation === ""
                    ? ""
                    : "is-valid"
                }`}
                name="transportation"
                as="select"
              >
                <option value="">Will you provide transportation?</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Field>
              <ErrorMessage
                name="transportation"
                component="p"
                className="invalid-feedback"
              />
            </div>
            <div className="form-group">
              <Field
                className={`form-control ${
                  touched.type && errors.type ? "is-invalid" : ""
                } ${errors.type || values.type === "" ? "" : "is-valid"}`}
                name="type"
                as="select"
              >
                <option value="">Requirement Type</option>
                <option value="Plasma">Plasma (for COVID-19 patient)</option>
                <option value="Blood">Blood</option>
              </Field>
              <ErrorMessage
                name="transportation"
                component="p"
                className="invalid-feedback"
              />
            </div>
            <div className="form-group">
              <Field
                className={`form-control ${
                  touched.hospital && errors.hospital ? "is-invalid" : ""
                } ${
                  errors.hospital || values.hospital === "" ? "" : "is-valid"
                }`}
                name="hospital"
                type="text"
                placeholder="Hospital name"
              />
              <ErrorMessage
                name="hospital"
                component="p"
                className="invalid-feedback"
              />
            </div>
            <div id="recaptcha-container"></div>
            <button
              className="btn btn-danger"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              ) : (
                "Submit"
              )}
            </button>
          </Form>
        )}
      </Formik>
      {!user ? (
        <div>
          <Link to="/register" className="submit-form-footer">
            If you want to make more than one request, consider registering as
            an Organization.
          </Link>
        </div>
      ) : null}
    </div>
  );
}
